/**
 * Admin lead dashboard.
 *
 * Lists every masterclass registration and strategy-session application with
 * status, timestamps, and the full UTM / attribution trail. Admin-only:
 * reads are gated by the `has_role(auth.uid(),'admin')` RLS policies, and the
 * UI checks the same role before querying.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Download, Loader2, RefreshCw, Search, ShieldAlert } from "lucide-react";
import { toast } from "sonner";

import { supabase } from "@/integrations/supabase/client";
import { LeadFollowupPanel, type LeadFollowup } from "@/components/admin/LeadFollowupPanel";
import { useAuth } from "@/hooks/useAuth";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";

const STATUSES = ["new", "contacted", "qualified", "won", "archived"] as const;
type LeadStatus = (typeof STATUSES)[number];

const STATUS_TONE: Record<string, string> = {
  new: "bg-amber-500/15 text-amber-600 border-amber-500/30",
  contacted: "bg-sky-500/15 text-sky-600 border-sky-500/30",
  qualified: "bg-violet-500/15 text-violet-600 border-violet-500/30",
  won: "bg-emerald-500/15 text-emerald-600 border-emerald-500/30",
  archived: "bg-muted text-muted-foreground border-border",
};

type BaseLead = {
  id: string;
  full_name: string;
  email: string;
  phone: string | null;
  source: string | null;
  page: string | null;
  status: string;
  submitted_at: string;
  created_at: string;
  updated_at: string;
  submission_count: number;
  first_submitted_at: string | null;
  last_submitted_at: string | null;
  utm_source: string | null;
  utm_medium: string | null;
  utm_campaign: string | null;
  utm_term: string | null;
  utm_content: string | null;
  ft_utm_source: string | null;
  ft_utm_medium: string | null;
  ft_utm_campaign: string | null;
  ft_utm_term: string | null;
  ft_utm_content: string | null;
  gclid: string | null;
  fbclid: string | null;
  msclkid: string | null;
  ttclid: string | null;
  referrer: string | null;
  landing_page: string | null;
  page_url: string | null;
  query_string: string | null;
  user_agent: string | null;
};

type MasterclassLead = BaseLead & { goal: string | null };

type StrategyLead = BaseLead & {
  organization: string | null;
  role_title: string | null;
  focus: string | null;
  current_stage: string | null;
  desired_outcome: string | null;
  timeline: string | null;
  budget: string | null;
  decision_authority: string | null;
  situation: string | null;
  timezone: string | null;
  time_on_form_ms: number | null;
};

type TableName = "masterclass_registrations" | "strategy_session_submissions";

const fmt = (value: string | null) =>
  value ? new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—";

const dash = (v: string | number | null | undefined) =>
  v === null || v === undefined || v === "" ? "—" : String(v);

const Field = ({ label, value }: { label: string; value: string | number | null }) => (
  <div className="min-w-0">
    <dt className="text-[11px] uppercase tracking-wider text-muted-foreground">{label}</dt>
    <dd className="truncate text-sm" title={value ? String(value) : undefined}>
      {dash(value)}
    </dd>
  </div>
);

const AttributionGrid = ({ row }: { row: BaseLead }) => (
  <div className="mt-4 rounded-md border border-border bg-muted/30 p-3">
    <p className="mb-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
      Attribution
    </p>
    <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-3 lg:grid-cols-5">
      <Field label="utm_source" value={row.utm_source} />
      <Field label="utm_medium" value={row.utm_medium} />
      <Field label="utm_campaign" value={row.utm_campaign} />
      <Field label="utm_term" value={row.utm_term} />
      <Field label="utm_content" value={row.utm_content} />
      <Field label="ft utm_source" value={row.ft_utm_source} />
      <Field label="ft utm_medium" value={row.ft_utm_medium} />
      <Field label="ft utm_campaign" value={row.ft_utm_campaign} />
      <Field label="ft utm_term" value={row.ft_utm_term} />
      <Field label="ft utm_content" value={row.ft_utm_content} />
      <Field label="gclid" value={row.gclid} />
      <Field label="fbclid" value={row.fbclid} />
      <Field label="msclkid" value={row.msclkid} />
      <Field label="ttclid" value={row.ttclid} />
      <Field label="landing page" value={row.landing_page} />
      <Field label="referrer" value={row.referrer} />
      <Field label="page URL" value={row.page_url} />
      <Field label="query string" value={row.query_string} />
      <Field label="user agent" value={row.user_agent} />
    </dl>
  </div>
);

/** Repeat submissions are merged into one record by the backend dedupe rules. */
const RepeatBadge = ({ count }: { count: number }) =>
  count > 1 ? (
    <Badge variant="outline" className="border-orange-500/30 bg-orange-500/15 text-orange-600">
      {count}× submitted
    </Badge>
  ) : null;

const StatusPicker = ({
  value,
  busy,
  onChange,
}: {
  value: string;
  busy: boolean;
  onChange: (next: LeadStatus) => void;
}) => (
  <Select value={STATUSES.includes(value as LeadStatus) ? value : "new"} onValueChange={(v) => onChange(v as LeadStatus)}>
    <SelectTrigger className="h-8 w-[150px]" disabled={busy} aria-label="Lead status">
      <SelectValue />
    </SelectTrigger>
    <SelectContent>
      {STATUSES.map((s) => (
        <SelectItem key={s} value={s}>
          {s}
        </SelectItem>
      ))}
    </SelectContent>
  </Select>
);

function toCsv(rows: Record<string, unknown>[]): string {
  if (rows.length === 0) return "";
  const keys = Array.from(new Set(rows.flatMap((r) => Object.keys(r)))).filter((k) => k !== "attribution");
  const esc = (v: unknown) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  return [keys.join(","), ...rows.map((r) => keys.map((k) => esc(r[k])).join(","))].join("\n");
}

function downloadCsv(name: string, rows: Record<string, unknown>[]) {
  const csv = toCsv(rows);
  if (!csv) {
    toast.error("Nothing to export");
    return;
  }
  const url = URL.createObjectURL(new Blob([csv], { type: "text/csv;charset=utf-8" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

const AdminLeads = () => {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState<boolean | null>(null);
  const [masterclass, setMasterclass] = useState<MasterclassLead[]>([]);
  const [strategy, setStrategy] = useState<StrategyLead[]>([]);
  const [loading, setLoading] = useState(false);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState<string>("all");

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      return;
    }
    supabase
      .rpc("has_role", { _user_id: user.id, _role: "admin" })
      .then(({ data, error }) => setIsAdmin(!error && data === true));
  }, [authLoading, user]);

  const [followups, setFollowups] = useState<Record<string, LeadFollowup>>({});
  const load = useCallback(async () => {
    setLoading(true);
    const [m, s, f] = await Promise.all([
      supabase.from("masterclass_registrations").select("*").order("created_at", { ascending: false }),
      supabase.from("strategy_session_submissions").select("*").order("created_at", { ascending: false }),
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      (supabase as any).from("lead_followups").select("*"),
    ]);
    if (m.error || s.error) toast.error(m.error?.message ?? s.error?.message ?? "Failed to load leads");
    setMasterclass((m.data as MasterclassLead[]) ?? []);
    setStrategy((s.data as StrategyLead[]) ?? []);
    const map: Record<string, LeadFollowup> = {};
    for (const row of (f.data as LeadFollowup[]) ?? []) map[`${row.lead_table}:${row.lead_id}`] = row;
    setFollowups(map);
    setLoading(false);
  }, []);

  useEffect(() => {
    if (isAdmin) void load();
  }, [isAdmin, load]);

  const matches = useCallback(
    (row: BaseLead & Record<string, unknown>) => {
      if (statusFilter !== "all" && row.status !== statusFilter) return false;
      const q = query.trim().toLowerCase();
      if (!q) return true;
      return [row.full_name, row.email, row.utm_source, row.utm_campaign, row.utm_medium, row.source]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q));
    },
    [query, statusFilter],
  );

  const visibleMasterclass = useMemo(() => masterclass.filter(matches), [masterclass, matches]);
  const visibleStrategy = useMemo(() => strategy.filter(matches), [strategy, matches]);

  const setStatus = async (table: TableName, id: string, next: LeadStatus) => {
    setBusyId(id);
    const { error } = await supabase.from(table).update({ status: next }).eq("id", id);
    setBusyId(null);
    if (error) {
      toast.error(error.message);
      return;
    }
    toast.success(`Marked ${next}`);
    void load();
  };

  if (authLoading || isAdmin === null) {
    return (
      <main className="flex min-h-screen items-center justify-center">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
      </main>
    );
  }

  if (!isAdmin) {
    return (
      <main className="mx-auto flex min-h-screen max-w-md flex-col items-center justify-center gap-4 px-6 text-center">
        <ShieldAlert className="h-8 w-8 text-muted-foreground" />
        <h1 className="text-xl font-semibold">Admin access required</h1>
        <p className="text-sm text-muted-foreground">
          Sign in with an administrator account to review lead submissions.
        </p>
        <Button asChild>
          <Link to="/auth">Sign in</Link>
        </Button>
      </main>
    );
  }

  return (
    <main className="mx-auto max-w-6xl px-4 py-10">
      <Helmet>
        <title>Lead Submissions | Admin</title>
        <meta name="description" content="Internal admin view of masterclass and strategy-session lead submissions." />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <header className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">Lead submissions</h1>
          <p className="text-sm text-muted-foreground">
            {masterclass.length} masterclass · {strategy.length} strategy-session records, with full campaign attribution.
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => void load()} disabled={loading}>
          {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCw className="mr-2 h-4 w-4" />}
          Refresh
        </Button>
      </header>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <div className="relative min-w-[240px] flex-1">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search name, email, campaign…"
            aria-label="Search leads"
            className="pl-9"
          />
        </div>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="w-[160px]" aria-label="Filter by status">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All statuses</SelectItem>
            {STATUSES.map((s) => (
              <SelectItem key={s} value={s}>
                {s}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Tabs defaultValue="masterclass">
        <TabsList>
          <TabsTrigger value="masterclass">Masterclass ({visibleMasterclass.length})</TabsTrigger>
          <TabsTrigger value="strategy">Strategy session ({visibleStrategy.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="masterclass" className="mt-4 space-y-4">
          <Button
            variant="outline"
            size="sm"
            onClick={() => downloadCsv("masterclass-registrations", visibleMasterclass)}
          >
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          {visibleMasterclass.length === 0 && (
            <p className="text-sm text-muted-foreground">No masterclass registrations match this view.</p>
          )}
          {visibleMasterclass.map((row) => (
            <Card key={row.id}>
              <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
                <div className="min-w-0">
                  <CardTitle className="text-base">{row.full_name}</CardTitle>
                  <p className="truncate text-sm text-muted-foreground">{row.email}</p>
                </div>
                <div className="flex items-center gap-2">
                  <RepeatBadge count={row.submission_count} />
                  <Badge variant="outline" className={STATUS_TONE[row.status] ?? ""}>
                    {row.status}
                  </Badge>
                  <StatusPicker
                    value={row.status}
                    busy={busyId === row.id}
                    onChange={(next) => void setStatus("masterclass_registrations", row.id, next)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                  <Field label="Submitted" value={fmt(row.submitted_at)} />
                  <Field label="Created" value={fmt(row.created_at)} />
                  <Field label="Updated" value={fmt(row.updated_at)} />
                  <Field label="First submitted" value={fmt(row.first_submitted_at)} />
                  <Field label="Last submitted" value={fmt(row.last_submitted_at)} />
                  <Field label="Times submitted" value={row.submission_count} />
                  <Field label="Phone" value={row.phone} />
                  <Field label="Goal" value={row.goal} />
                  <Field label="Source" value={row.source} />
                  <Field label="Page" value={row.page} />
                </dl>
                <LeadFollowupPanel
                  table="masterclass_registrations"
                  leadId={row.id}
                  email={row.email}
                  followup={followups[`masterclass_registrations:${row.id}`]}
                  onChanged={() => void load()}
                />
                <AttributionGrid row={row} />
              </CardContent>
            </Card>
          ))}
        </TabsContent>

        <TabsContent value="strategy" className="mt-4 space-y-4">
          <Button variant="outline" size="sm" onClick={() => downloadCsv("strategy-sessions", visibleStrategy)}>
            <Download className="mr-2 h-4 w-4" /> Export CSV
          </Button>
          {visibleStrategy.length === 0 && (
            <p className="text-sm text-muted-foreground">No strategy-session applications match this view.</p>
          )}
          {visibleStrategy.map((row) => (
            <Card key={row.id}>
              <CardHeader className="flex flex-row flex-wrap items-start justify-between gap-3 space-y-0">
                <div className="min-w-0">
                  <CardTitle className="text-base">{row.full_name}</CardTitle>
                  <p className="truncate text-sm text-muted-foreground">
                    {row.email}
                    {row.organization ? ` · ${row.organization}` : ""}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <RepeatBadge count={row.submission_count} />
                  <Badge variant="outline" className={STATUS_TONE[row.status] ?? ""}>
                    {row.status}
                  </Badge>
                  <StatusPicker
                    value={row.status}
                    busy={busyId === row.id}
                    onChange={(next) => void setStatus("strategy_session_submissions", row.id, next)}
                  />
                </div>
              </CardHeader>
              <CardContent>
                <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
                  <Field label="Submitted" value={fmt(row.submitted_at)} />
                  <Field label="Created" value={fmt(row.created_at)} />
                  <Field label="Updated" value={fmt(row.updated_at)} />
                  <Field label="First submitted" value={fmt(row.first_submitted_at)} />
                  <Field label="Last submitted" value={fmt(row.last_submitted_at)} />
                  <Field label="Times submitted" value={row.submission_count} />
                  <Field label="Phone" value={row.phone} />
                  <Field label="Role" value={row.role_title} />
                  <Field label="Focus" value={row.focus} />
                  <Field label="Stage" value={row.current_stage} />
                  <Field label="Timeline" value={row.timeline} />
                  <Field label="Budget" value={row.budget} />
                  <Field label="Decision authority" value={row.decision_authority} />
                  <Field label="Timezone" value={row.timezone} />
                  <Field label="Time on form" value={row.time_on_form_ms ? `${Math.round(row.time_on_form_ms / 1000)}s` : null} />
                  <Field label="Source" value={row.source} />
                  <Field label="Page" value={row.page} />
                </dl>
                {(row.desired_outcome || row.situation) && (
                  <div className="mt-4 space-y-2 text-sm">
                    {row.desired_outcome && (
                      <p>
                        <span className="text-muted-foreground">Desired outcome: </span>
                        {row.desired_outcome}
                      </p>
                    )}
                    {row.situation && (
                      <p>
                        <span className="text-muted-foreground">Situation: </span>
                        {row.situation}
                      </p>
                    )}
                  </div>
                )}
                <LeadFollowupPanel
                  table="strategy_session_submissions"
                  leadId={row.id}
                  email={row.email}
                  followup={followups[`strategy_session_submissions:${row.id}`]}
                  onChanged={() => void load()}
                />
                <AttributionGrid row={row} />
              </CardContent>
            </Card>
          ))}
        </TabsContent>
      </Tabs>
    </main>
  );
};

export default AdminLeads;
